/**
 * Plan-of-care progress (2.5): one bar per discipline group with an order on the
 * plan — delivered against ordered for the certification period — beside the
 * period line that carries the day of the cert period, the 30-day payment-period
 * boundary and the recert window, and the pace read the Act 3 guardrails use.
 *
 * The numbers come from `selectPlanProgress`; nothing is computed here.
 */
import { useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { certPeriodFraction, selectPlanProgress, type GroupProgress, type PlanProgress } from "../../engine/planProgress";
import { dateKeyStart, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { useDemoState } from "../../store/useDemoStore";

const DELIVERED = "#6fa36f";
const SCHEDULED = "#7aa6d0";

export function PlanProgressSection({ patientRef }: { patientRef: string }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const progress = useMemo(() => selectPlanProgress(state, patientRef, today), [state, patientRef, today]);
  if (!progress) return null;
  const fmt = (d: string, p = "MMM d") => formatInBranchZone(dateKeyStart(d, tz), tz, p);

  return (
    <Box data-plan-progress={progress.episodeId}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6">Plan of care</Typography>
        <Typography variant="body2" color="text.secondary">
          Day {progress.dayOfCert} of {progress.certPeriodDays} · SOC {fmt(progress.socDate, "EEE MMM d")} · through {fmt(progress.certEndDate, "EEE MMM d")}
        </Typography>
        {/* No pace chip and no period chip (Pete, through the script author,
            discussion turn 61). The first was the track table's first column
            said again. The second was the LUPA read — a real number, and a
            standing badge in a word nobody in the room uses, inviting a
            payment-rule conversation on the patient page. The idea lives in the
            2.1 talk track, where it needs no number; `selectOnPace` is untouched
            and the read is a worklist row away whenever somebody wants it. */}
      </Stack>

      <Stack spacing={1} sx={{ maxWidth: 720 }}>
        {progress.groups.map((g) => (
          <ProgressBar key={g.group} group={g} />
        ))}
        {progress.groups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No orders on the plan yet.
          </Typography>
        )}
      </Stack>

      <PeriodLine progress={progress} today={today} timeZone={tz} />
    </Box>
  );
}

function ProgressBar({ group }: { group: GroupProgress }) {
  const total = Math.max(group.ordered, 1);
  // A discipline still to plan has no denominator worth printing: the count it is
  // measured against arrives with its orders.
  const deliveredPct = group.pending ? 0 : Math.min(100, (group.delivered / total) * 100);
  const scheduledPct = group.pending ? 0 : Math.min(100 - deliveredPct, (group.scheduled / total) * 100);
  const label = group.pending
    ? `${group.group} ${group.delivered ? `${group.delivered} delivered · ` : ""}frequencies to follow`
    : `${group.group} ${group.delivered} of ${group.ordered}`;
  return (
    <Box data-progress-group={group.group}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 92 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {group.pending ? (group.pendingWeeks ? `${group.pendingWeeks} weeks reserved on the branch read` : "ordered, no frequencies yet") : `${group.scheduled} planned`}
        </Typography>
      </Stack>
      <Tooltip title={group.pending ? "Ordered, with no frequencies yet — the branch read carries it as reserved, awaiting plan, until the orders land." : `${group.delivered} delivered · ${group.scheduled} planned · ${group.ordered} ordered this certification period`}>
        <Box
          sx={{
            mt: 0.25,
            height: 12,
            borderRadius: 0.5,
            border: "1px solid #ccc",
            display: "flex",
            overflow: "hidden",
            backgroundImage: group.pending ? "repeating-linear-gradient(135deg, #e0e0e0 0 3px, #fafafa 3px 6px)" : undefined,
            bgcolor: group.pending ? undefined : "#f2f2f2",
          }}
        >
          <Box data-fill="delivered" sx={{ width: `${deliveredPct}%`, bgcolor: DELIVERED }} />
          <Box data-fill="scheduled" sx={{ width: `${scheduledPct}%`, backgroundImage: `repeating-linear-gradient(135deg, ${SCHEDULED} 0 4px, #cddcea 4px 8px)` }} />
        </Box>
      </Tooltip>
    </Box>
  );
}

/** The cert period as a line: today's tick, the 30-day boundaries, the recert window shaded. */
function PeriodLine({ progress, today, timeZone }: { progress: PlanProgress; today: string; timeZone: string }) {
  const fmt = (d: string, p = "MMM d") => formatInBranchZone(dateKeyStart(d, timeZone), timeZone, p);
  const left = (d: string) => `${certPeriodFraction(progress, d) * 100}%`;
  const recertStart = certPeriodFraction(progress, progress.recert.start);
  const recertEnd = certPeriodFraction(progress, progress.recert.end);
  return (
    <Box sx={{ mt: 2, maxWidth: 720 }}>
      <Box sx={{ position: "relative", height: 10, bgcolor: "#f2f2f2", border: "1px solid #ddd", borderRadius: 0.5 }}>
        <Tooltip title={`Recert window · days 56–60 · ${fmt(progress.recert.start)} – ${fmt(progress.recert.end)}`}>
          <Box data-tick="recert" sx={{ position: "absolute", top: 0, bottom: 0, left: `${recertStart * 100}%`, width: `${Math.max(recertEnd - recertStart, 0.02) * 100}%`, backgroundImage: "repeating-linear-gradient(135deg, #e0a13a 0 3px, #fff4d6 3px 6px)" }} />
        </Tooltip>
        {progress.periods.slice(1).map((p) => (
          <Tooltip key={p.index} title={`Payment period ${p.index + 1} begins ${fmt(p.start)}`}>
            <Box data-tick="period" sx={{ position: "absolute", top: -3, bottom: -3, left: left(p.start), width: "2px", bgcolor: "#666" }} />
          </Tooltip>
        ))}
        <Tooltip title={`Today · day ${progress.dayOfCert} of ${progress.certPeriodDays}`}>
          <Box data-tick="today" sx={{ position: "absolute", top: -4, bottom: -4, left: left(today), width: "2px", bgcolor: "#c62828" }} />
        </Tooltip>
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          SOC {fmt(progress.socDate)}
        </Typography>
        {progress.periods.slice(1).map((p) => (
          <Typography key={p.index} variant="caption" color="text.secondary">
            Period {p.index + 1} from {fmt(p.start)}
          </Typography>
        ))}
        <Typography variant="caption" color="text.secondary">
          Recert window {fmt(progress.recert.start)} – {fmt(progress.recert.end)}
        </Typography>
      </Stack>
    </Box>
  );
}
